import { useHandler } from "@redotech/react-util/hook";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoCurrencySelector } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-currency-selector";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { InfoTooltip } from "@redotech/redo-web/info-tooltip";
import { memo } from "react";

const DEFAULT_CURRENCIES = ["AUD", "GBP", "CAD", "EUR", "JPY"];

/**
 * Reusable per-currency amount overrides UI. Used wherever a single base
 * amount (in the shop's currency) can be overridden with exact amounts for
 * specific presentment currencies.
 */
export const PerCurrencyOverrides = memo(function PerCurrencyOverrides({
  baseAmount,
  currencyOverrides,
  onCurrencyOverridesChange,
  perCurrencyAmountsEnabled,
  onPerCurrencyAmountsEnabledChange,
  disabled,
  tooltipText = "Specify exact amounts for specific currencies. Currencies not listed will be converted from the default amount above using live exchange rates.",
}: {
  /** The base flat amount string — used to determine sign (fee vs bonus). */
  baseAmount: string;
  currencyOverrides: Record<string, string>;
  onCurrencyOverridesChange(overrides: Record<string, string>): void;
  perCurrencyAmountsEnabled: boolean;
  onPerCurrencyAmountsEnabledChange(enabled: boolean): void;
  disabled: boolean;
  tooltipText?: string;
}) {
  const isNegativeBase = parseFloat(baseAmount) < 0;

  const handleAddCurrency = useHandler(() => {
    const existing = Object.keys(currencyOverrides);
    const nextCurrency =
      DEFAULT_CURRENCIES.find((c) => !existing.includes(c)) ?? "USD";
    onCurrencyOverridesChange({ ...currencyOverrides, [nextCurrency]: "0.00" });
  });

  return (
    <Flex dir="column" gap="sm">
      <Flex align="center" gap="xs">
        <RedoToggle
          disabled={disabled}
          label="Per-currency amounts"
          setValue={onPerCurrencyAmountsEnabledChange}
          size="xs"
          value={perCurrencyAmountsEnabled}
        />
        <InfoTooltip boxSize={16} tooltip={{ title: tooltipText }} />
      </Flex>
      {perCurrencyAmountsEnabled && (
        <Flex dir="column" gap="lg">
          {Object.entries(currencyOverrides).map(([currency, amount]) => {
            const displayOverrideValue = Math.abs(
              parseFloat(amount) || 0,
            ).toString();
            return (
              <Flex align="center" dir="row" gap="sm" key={currency}>
                <Flex dir="column" flex={1} gap="sm">
                  <RedoCurrencySelector
                    disabled={disabled}
                    label=""
                    setValue={(newCurrency) => {
                      const updated = { ...currencyOverrides };
                      delete updated[currency];
                      updated[newCurrency] = amount;
                      onCurrencyOverridesChange(updated);
                    }}
                    value={currency}
                  />
                  <RedoTextInput
                    fullWidth
                    inputTypeHint="number"
                    label=""
                    onBlur={() => {
                      const updated = { ...currencyOverrides };
                      const num = parseFloat(amount);
                      const signed = isNaN(num)
                        ? 0
                        : isNegativeBase
                          ? -Math.abs(num)
                          : Math.abs(num);
                      updated[currency] = signed.toFixed(2);
                      onCurrencyOverridesChange(updated);
                    }}
                    setValue={(val) => {
                      const updated = { ...currencyOverrides };
                      const num = parseFloat(val);
                      const signed = isNegativeBase
                        ? -Math.abs(num)
                        : Math.abs(num);
                      updated[currency] = isNaN(num) ? "0" : String(signed);
                      onCurrencyOverridesChange(updated);
                    }}
                    state={disabled ? "readonly" : "default"}
                    value={displayOverrideValue}
                  />
                </Flex>
                {!disabled && (
                  <RedoButton
                    hierarchy="tertiary"
                    IconLeading={TrashIcon}
                    onClick={() => {
                      const updated = { ...currencyOverrides };
                      delete updated[currency];
                      onCurrencyOverridesChange(updated);
                    }}
                    size="xs"
                  />
                )}
              </Flex>
            );
          })}
          {!disabled && (
            <RedoButton
              hierarchy="tertiary"
              IconLeading={PlusIcon}
              onClick={handleAddCurrency}
              size="xs"
              text="Add currency"
            />
          )}
        </Flex>
      )}
    </Flex>
  );
});
